<template>
    <HomeSectionContainer title="Integrations">
        <template #subtitle>
            <p class="body-small text-ink-muted mt-1">Connect your tools so Nadia can give you more relevant, in-the-moment coaching.</p>
        </template>
        <div class="flex flex-col gap-3 mt-2">
            <template v-if="integrationsEnabled">
                <!-- Calendar -->
                <IntegrationCard
                    v-if="calendarIntegration?.enabled"
                    name="Calendar"
                    :description="calendarConnected ? `${providerLabel} calendar connected` : 'See your upcoming meetings in context'"
                    :connected="calendarConnected"
                    @connect="showCalendarSettings = true"
                    @settings="showCalendarSettings = true">
                    <template #icon>
                        <div class="size-10 flex items-center justify-center rounded-xl bg-surface-sunken">
                            <i class="bi bi-calendar2-week text-ink-muted text-lg" aria-hidden="true"></i>
                        </div>
                    </template>
                </IntegrationCard>
                <CalendarIntegrationSettings
                    v-if="calendarIntegration?.enabled"
                    v-model:open="showCalendarSettings"
                    source="profile_integrations"
                    @close="showCalendarSettings = false" />

                <!-- Granola -->
                <IntegrationCard
                    v-if="granolaEnabled"
                    name="Granola"
                    badge="MCP"
                    :description="granolaConnected ? 'Meeting notes connected via MCP' : 'Ask Nadia about your recent meetings and get coaching from them'"
                    :connected="granolaConnected"
                    @connect="connectGranola"
                    @settings="disconnectGranola">
                    <template #icon>
                        <div class="size-10 flex items-center justify-center rounded-xl bg-surface-sunken">
                            <svg aria-hidden="true" width="22" height="22" viewBox="0 0 32 32" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <rect width="32" height="32" rx="8" fill="#16A34A" opacity="0.15" />
                                <text x="16" y="22" text-anchor="middle" font-family="system-ui, sans-serif" font-size="18" font-weight="700" fill="#16A34A">G</text>
                            </svg>
                        </div>
                    </template>
                </IntegrationCard>

                <!-- Work IQ (Microsoft 365) -->
                <IntegrationCard
                    v-if="workiqIntegration?.enabled"
                    name="Work IQ"
                    badge="Preview"
                    description="Bring your Microsoft 365 work context into coaching"
                    :connected="workiqIntegration?.connected"
                    @connect="connectWorkIq"
                    @settings="showWorkIqDisconnect = true">
                    <template #icon>
                        <div class="size-10 flex items-center justify-center rounded-xl bg-surface-sunken">
                            <i class="bi bi-microsoft text-workiq-microsoft-brand text-lg" aria-hidden="true"></i>
                        </div>
                    </template>
                </IntegrationCard>
                <WorkIqIntegrationConsent v-if="workiqIntegration?.enabled && !workiqIntegration?.connected" v-model:open="showWorkIqConsent" @confirm="redirectToWorkIqConnect" />
                <WorkIqIntegrationDisconnect
                    v-if="workiqIntegration?.enabled && workiqIntegration?.connected"
                    v-model:open="showWorkIqDisconnect"
                    :disconnect-url="workiqIntegration?.disconnect_url ?? ''"
                    :auto-context="workiqIntegration?.auto_context ?? false"
                    :sources="workiqIntegration?.sources ?? []" />

                <!-- Slack -->
                <IntegrationCard
                    v-if="slackAppUrl"
                    name="Slack"
                    description="Connect with Nadia in Slack"
                    :connected="slackConnected"
                    @connect="openSlackApp"
                    @settings="showSlackSettings = true">
                    <template #icon>
                        <div class="size-10 flex items-center justify-center rounded-xl bg-surface-sunken">
                            <svg aria-hidden="true" width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path
                                    d="M5.042 15.165a2.528 2.528 0 0 1-2.52 2.523A2.528 2.528 0 0 1 0 15.165a2.527 2.527 0 0 1 2.522-2.52h2.52v2.52zm1.271 0a2.527 2.527 0 0 1 2.521-2.52 2.527 2.527 0 0 1 2.521 2.52v6.313A2.528 2.528 0 0 1 8.834 24a2.528 2.528 0 0 1-2.521-2.522v-6.313z"
                                    fill="#E01E5A" />
                                <path
                                    d="M8.834 5.042a2.528 2.528 0 0 1-2.521-2.52A2.528 2.528 0 0 1 8.834 0a2.528 2.528 0 0 1 2.521 2.522v2.52H8.834zm0 1.271a2.528 2.528 0 0 1 2.521 2.521 2.528 2.528 0 0 1-2.521 2.521H2.522A2.528 2.528 0 0 1 0 8.834a2.528 2.528 0 0 1 2.522-2.521h6.312z"
                                    fill="#36C5F0" />
                                <path
                                    d="M18.956 8.834a2.528 2.528 0 0 1 2.522-2.521A2.528 2.528 0 0 1 24 8.834a2.528 2.528 0 0 1-2.522 2.521h-2.522V8.834zm-1.27 0a2.528 2.528 0 0 1-2.522 2.521 2.527 2.527 0 0 1-2.521-2.521V2.522A2.527 2.527 0 0 1 15.165 0a2.528 2.528 0 0 1 2.522 2.522v6.312z"
                                    fill="#2EB67D" />
                                <path
                                    d="M15.165 18.956a2.528 2.528 0 0 1 2.522 2.522A2.528 2.528 0 0 1 15.165 24a2.527 2.527 0 0 1-2.521-2.522v-2.522h2.521zm0-1.27a2.527 2.527 0 0 1-2.521-2.522 2.528 2.528 0 0 1 2.521-2.521h6.313A2.528 2.528 0 0 1 24 15.165a2.528 2.528 0 0 1-2.522 2.521h-6.313z"
                                    fill="#ECB22E" />
                            </svg>
                        </div>
                    </template>
                </IntegrationCard>
                <SlackIntegrationSettings v-if="slackAppUrl && slackConnected" v-model:open="showSlackSettings" :workspaces="slackWorkspaces" />
            </template>

            <IntegrationCard
                v-if="mobileAppDownloadUrl"
                name="Mobile app"
                description="Take Nadia with you for coaching wherever you are"
                action-label="Get the app"
                :action-href="mobileAppDownloadUrl"
                :show-status="false">
                <template #icon>
                    <div class="size-10 flex items-center justify-center rounded-xl bg-surface-sunken">
                        <i class="bi bi-phone text-ink text-lg" aria-hidden="true"></i>
                    </div>
                </template>
            </IntegrationCard>
        </div>
    </HomeSectionContainer>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import type { UserDetails } from "~vue/appTypes";
import HomeSectionContainer from "~vue/ChatHome/HomeSectionContainer.vue";
import CalendarIntegrationSettings from "~vue/components/CalendarIntegration/CalendarIntegrationSettings.vue";
import IntegrationCard from "~vue/components/Integrations/IntegrationCard.vue";
import SlackIntegrationSettings, { type SlackWorkspaceLink } from "~vue/components/SlackIntegration/SlackIntegrationSettings.vue";
import WorkIqIntegrationConsent from "~vue/components/WorkIqIntegration/WorkIqIntegrationConsent.vue";
import WorkIqIntegrationDisconnect from "~vue/components/WorkIqIntegration/WorkIqIntegrationDisconnect.vue";
import type { Ref } from "vue";
import { computed, inject, ref } from "vue";

const props = withDefaults(
    defineProps<{
        calendarIntegration?: Partial<{
            enabled: boolean;
            calendar_provider: string | null;
        }>;
        granolaEnabled?: boolean;
        granolaConnected?: boolean;
        granolaConnectUrl?: string;
        granolaDisconnectUrl?: string;
        workiqIntegration?: Partial<{
            enabled: boolean;
            connected: boolean;
            connect_url: string;
            status_url: string;
            disconnect_url: string;
            auto_context: boolean;
            sources: { source: string; key: string; label: string; description: string; enabled: boolean }[];
        }>;
        slackAppUrl?: string;
        slackConnected?: boolean;
        slackWorkspaces?: SlackWorkspaceLink[];
        integrationsEnabled?: boolean;
        openCalendarOnMount?: boolean;
        mobileAppDownloadUrl?: string;
    }>(),
    {
        calendarIntegration: () => ({}),
        granolaEnabled: false,
        granolaConnected: false,
        granolaConnectUrl: "/api/external-context/granola/connect/",
        granolaDisconnectUrl: "",
        workiqIntegration: () => ({}),
        slackAppUrl: "",
        slackConnected: false,
        slackWorkspaces: () => [],
        integrationsEnabled: false,
        openCalendarOnMount: false,
        mobileAppDownloadUrl: "",
    },
);

const userDetails = inject<Ref<UserDetails>>("user_details");
const showCalendarSettings = ref(props.openCalendarOnMount);
const showWorkIqDisconnect = ref(false);
const showSlackSettings = ref(false);
const showWorkIqConsent = ref(false);

function connectWorkIq() {
    if (!props.workiqIntegration?.connect_url) {
        return;
    }
    showWorkIqConsent.value = true;
}

function redirectToWorkIqConnect() {
    const connectUrl = props.workiqIntegration?.connect_url;
    if (!connectUrl) {
        return;
    }
    const next = encodeURIComponent(globalThis.location.pathname);
    globalThis.location.href = `${connectUrl}?next=${next}`;
}

function openSlackApp() {
    if (props.slackAppUrl) {
        globalThis.open(props.slackAppUrl, "_blank", "noopener,noreferrer");
    }
}

const calendarConnected = computed(() => !!userDetails?.value?.has_calendar_integration);

const providerLabel = computed(() => {
    const provider = props.calendarIntegration?.calendar_provider;
    if (provider === "google") {
        return "Google";
    }
    if (provider === "microsoft") {
        return "Microsoft";
    }
    return "Calendar";
});

function connectGranola() {
    globalThis.location.href = props.granolaConnectUrl;
}

const { execute: executeDisconnect } = useFetch(() => props.granolaDisconnectUrl, { immediate: false }).post();

async function disconnectGranola() {
    if (!props.granolaDisconnectUrl) {
        return;
    }
    await executeDisconnect();
    globalThis.location.reload();
}
</script>
